import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DUPLICATE_STEP_IDS_DELETE_REASON } from 'constants/uiConstants';
import type { IInputStep, IInputStepSeedDress } from 'interfaces/IInputStep';
import { InputStepType, SeedDressRateMode } from 'interfaces/IInputStep';
import type { IInputTask } from 'interfaces/IInputTask';

import { hasDuplicateStepIds } from './hasDuplicateStepIds';
import {
  buildSeedLinkedReason,
  getLinkedSeedDressItems,
  getTaskSteps,
} from './stepCalculations';
import { filterStepsByCategory } from './stepCategoryFilters';

// Operations (Application/Generic) have no product metadata worth surfacing,
// so "View Details" is hidden for them.
const VIEW_DETAILS_EXCLUDED_STEP_TYPES = new Set<InputStepType>([
  InputStepType.Generic,
  InputStepType.Application,
]);

type ContextMenuTarget =
  | {
      rowType: 'task';
      taskId: string;
    }
  | {
      rowType: 'step';
      taskId: string;
      stepId: string;
    };

interface ContextMenuState {
  x: number;
  y: number;
  // Right-click anchors the menu's left edge at the cursor; the row's
  // three-dot button anchors its right edge at the button instead, so the menu
  // hangs back over the grid rather than off the right-hand side of it.
  alignRight: boolean;
  target: ContextMenuTarget;
}

export type { ContextMenuTarget };

// Lets the three-dot button toggle the menu shut when it is already open on
// the same row, rather than reopening it in place.
const isSameContextMenuTarget = (
  a: ContextMenuTarget,
  b: ContextMenuTarget
): boolean => {
  if (a.rowType !== b.rowType || a.taskId !== b.taskId) return false;
  return a.rowType === 'step' && b.rowType === 'step'
    ? a.stepId === b.stepId
    : true;
};

interface UseStepContextMenuParams {
  tasks: IInputTask[];
  activeStepCategoryKey: string;
  removeDraftTask: (taskId: string) => void;
  schedulePendingTaskDelete: (taskId: string) => void;
  updateDraftTask: (task: IInputTask) => void;
  schedulePendingTaskSave: (task: IInputTask) => void;
  closeInputSelectors: () => void;
  onReorder: (taskId: string, stepId: string) => void;
}

// Owns the right-click/kebab context menu shared by task and step rows —
// its open/close state, which row it targets, and the derived "is this menu
// item allowed" checks (View Details, Reorder, Delete) that depend on it.
export const useStepContextMenu = ({
  tasks,
  activeStepCategoryKey,
  removeDraftTask,
  schedulePendingTaskDelete,
  updateDraftTask,
  schedulePendingTaskSave,
  closeInputSelectors,
  onReorder,
}: UseStepContextMenuParams) => {
  const [contextMenuState, setContextMenuState] =
    useState<ContextMenuState | null>(null);
  const [viewDetailsStep, setViewDetailsStep] = useState<IInputStep | null>(
    null
  );
  const [seedTargetEditTarget, setSeedTargetEditTarget] = useState<{
    taskId: string;
    stepId: string;
  } | null>(null);
  const contextMenuRef = useRef<HTMLDivElement | null>(null);

  const closeContextMenu = useCallback(() => {
    setContextMenuState(null);
  }, []);

  // A row's own onContextMenu handler stops propagation before this can fire,
  // so this only closes the menu on interactions outside it (including a
  // right-click elsewhere, which the row handler then reopens at the new
  // target in the same tick).
  useEffect(() => {
    if (!contextMenuState) return;
    const handleOutsideInteraction = (event: MouseEvent) => {
      if (
        contextMenuRef.current &&
        !contextMenuRef.current.contains(event.target as Node)
      ) {
        closeContextMenu();
      }
    };
    document.addEventListener('mousedown', handleOutsideInteraction);
    document.addEventListener('contextmenu', handleOutsideInteraction);
    return () => {
      document.removeEventListener('mousedown', handleOutsideInteraction);
      document.removeEventListener('contextmenu', handleOutsideInteraction);
    };
  }, [contextMenuState, closeContextMenu]);

  const findStepById = useCallback(
    (taskId: string, stepId: string): IInputStep | null => {
      const task = tasks.find((t) => t.taskId === taskId);
      if (!task) return null;
      return getTaskSteps(task).find((step) => step.stepId === stepId) ?? null;
    },
    [tasks]
  );

  const contextMenuStep = useMemo(() => {
    if (!contextMenuState || contextMenuState.target.rowType !== 'step') {
      return null;
    }
    return findStepById(
      contextMenuState.target.taskId,
      contextMenuState.target.stepId
    );
  }, [contextMenuState, findStepById]);

  const showViewDetails =
    !!contextMenuStep &&
    !VIEW_DETAILS_EXCLUDED_STEP_TYPES.has(contextMenuStep.stepType);

  const showChangeSeedTarget =
    contextMenuStep?.stepType === InputStepType.SeedDress &&
    (contextMenuStep as IInputStepSeedDress).rateMode ===
      SeedDressRateMode.Per100Kg;

  const handleViewDetailsFromContext = useCallback(() => {
    if (!contextMenuState || contextMenuState.target.rowType !== 'step') {
      return;
    }
    const step = findStepById(
      contextMenuState.target.taskId,
      contextMenuState.target.stepId
    );
    if (step) setViewDetailsStep(step);
    closeContextMenu();
  }, [closeContextMenu, contextMenuState, findStepById]);

  const handleChangeSeedTargetFromContext = useCallback(() => {
    if (!contextMenuState || contextMenuState.target.rowType !== 'step') {
      return;
    }
    const { taskId, stepId } = contextMenuState.target;
    setSeedTargetEditTarget({ taskId, stepId });
    closeContextMenu();
  }, [closeContextMenu, contextMenuState]);

  const closeSeedTargetEdit = useCallback(() => {
    setSeedTargetEditTarget(null);
  }, []);

  const handleTaskRowContextMenu = useCallback(
    (event: React.MouseEvent, taskId: string) => {
      event.preventDefault();
      event.stopPropagation();
      closeInputSelectors();
      setContextMenuState({
        x: event.clientX,
        y: event.clientY,
        alignRight: false,
        target: {
          rowType: 'task',
          taskId,
        },
      });
    },
    [closeInputSelectors]
  );

  const handleStepRowContextMenu = useCallback(
    (event: React.MouseEvent, taskId: string, stepId: string) => {
      event.preventDefault();
      event.stopPropagation();
      closeInputSelectors();
      setContextMenuState({
        x: event.clientX,
        y: event.clientY,
        alignRight: false,
        target: {
          rowType: 'step',
          taskId,
          stepId,
        },
      });
    },
    [closeInputSelectors]
  );

  // The three-dot button opens the very same menu as right-click, anchored to
  // the button rather than the cursor. Clicking it again on the row it is
  // already open for closes the menu.
  const openMenuFromKebab = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, target: ContextMenuTarget) => {
      event.preventDefault();
      event.stopPropagation();
      const { right, bottom } = event.currentTarget.getBoundingClientRect();
      closeInputSelectors();
      setContextMenuState((current) =>
        current && isSameContextMenuTarget(current.target, target)
          ? null
          : { x: right, y: bottom, alignRight: true, target }
      );
    },
    [closeInputSelectors]
  );

  const handleDeleteFromContext = useCallback(() => {
    if (!contextMenuState) return;

    if (contextMenuState.target.rowType === 'task') {
      const { taskId } = contextMenuState.target;
      removeDraftTask(taskId);
      schedulePendingTaskDelete(taskId);
      closeContextMenu();
      return;
    }

    const { taskId, stepId } = contextMenuState.target;
    const task = tasks.find((t) => t.taskId === taskId);
    if (task) {
      const steps = getTaskSteps(task);
      // The filter below removes *every* step carrying this id. Where ids
      // repeat that is silent, persisted data loss of a row the user never
      // selected, so the menu item is disabled and this refuses as well.
      if (hasDuplicateStepIds(steps)) {
        closeContextMenu();
        return;
      }
      const updatedTask: IInputTask = {
        ...task,
        steps: steps.filter((step) => step.stepId !== stepId),
      };
      updateDraftTask(updatedTask);
      schedulePendingTaskSave(updatedTask);
    }
    closeContextMenu();
  }, [
    closeContextMenu,
    contextMenuState,
    removeDraftTask,
    schedulePendingTaskDelete,
    schedulePendingTaskSave,
    tasks,
    updateDraftTask,
  ]);

  // Reordering is only meaningful when there is somewhere to move to, so the
  // menu item follows the same rule as the grip handle.
  const canReorder = useMemo(() => {
    const target = contextMenuState?.target;
    if (!target || target.rowType !== 'step') return false;
    const task = tasks.find((candidate) => candidate.taskId === target.taskId);
    if (!task) return false;
    const allSteps = getTaskSteps(task);
    // Same opt-out as the grip: with duplicated ids there is no way to say
    // which row the user means.
    if (hasDuplicateStepIds(allSteps)) return false;
    return filterStepsByCategory(allSteps, activeStepCategoryKey).length > 1;
  }, [contextMenuState, tasks, activeStepCategoryKey]);

  const handleReorderFromContext = useCallback(() => {
    const target = contextMenuState?.target;
    if (!target || target.rowType !== 'step') return;
    onReorder(target.taskId, target.stepId);
  }, [contextMenuState, onReorder]);

  // Deleting a step resolves it by id, so on a task whose ids repeat it would
  // take every match with it — losing an input the user never pointed at, and
  // persisting that. Only *step* deletion is ambiguous: deleting the task
  // itself is addressed by taskId and stays available.
  const deleteIsAmbiguous = useMemo(() => {
    const target = contextMenuState?.target;
    if (!target || target.rowType !== 'step') return false;
    const task = tasks.find((candidate) => candidate.taskId === target.taskId);
    if (!task) return false;
    return hasDuplicateStepIds(getTaskSteps(task));
  }, [contextMenuState, tasks]);

  // A Seed step that at least one SeedDress step holds a linkedStepId to
  // cannot be deleted — the SeedDress would be left with a dangling reference
  // and its /100kg rate calculation would silently break. Returns the
  // human-readable reason naming the specific blocking treatments, or
  // undefined when the step is not linked.
  const seedLinkedDeleteReason = useMemo(() => {
    const target = contextMenuState?.target;
    if (!target || target.rowType !== 'step') return undefined;
    const task = tasks.find((candidate) => candidate.taskId === target.taskId);
    if (!task) return undefined;
    const steps = getTaskSteps(task);
    const targetStep = steps.find((s) => s.stepId === target.stepId);
    if (targetStep?.stepType !== InputStepType.Seed) return undefined;
    const items = getLinkedSeedDressItems(task, target.stepId);
    if (items.length === 0) return undefined;
    return buildSeedLinkedReason(items);
  }, [contextMenuState, tasks]);

  const closeViewDetailsStep = useCallback(() => {
    setViewDetailsStep(null);
  }, []);

  const reset = useCallback(() => {
    setContextMenuState(null);
    setViewDetailsStep(null);
    setSeedTargetEditTarget(null);
  }, []);

  return {
    contextMenuState,
    contextMenuRef,
    contextMenuStep,
    viewDetailsStep,
    closeViewDetailsStep,
    showViewDetails,
    canReorder,
    deleteDisabledReason: deleteIsAmbiguous
      ? DUPLICATE_STEP_IDS_DELETE_REASON
      : seedLinkedDeleteReason,
    closeContextMenu,
    handleTaskRowContextMenu,
    handleStepRowContextMenu,
    openMenuFromKebab,
    handleViewDetailsFromContext,
    handleDeleteFromContext,
    handleReorderFromContext,
    showChangeSeedTarget,
    seedTargetEditTarget,
    handleChangeSeedTargetFromContext,
    closeSeedTargetEdit,
    reset,
  };
};
